import moment from 'moment'

// Pure logic for the two applicant job-title editors
// (components/entities/applicant/JobTitles.vue).
// Every back-end name of the ranked-titles contract lives in this file.

export const JOB_TITLE_RANKS_PATH = '/applicants/job_titles/ranks'
// 200 on the new back-end; today's back-end has no such route (404).
export const jobTitleProfilePath = applicantId => `/applicants/job_titles/profile/${applicantId}`
export const MAX_RANKED = 5
export const MAX_YEARS = 60

export const INTEREST = {
  INTERESTED: 'interested',
  NOT_INTERESTED: 'not_interested',
  UNKNOWN: 'unknown'
}

export const DEFAULT_PROFILE = Object.freeze({ ranks_owner: null, ranks_version: 0, primary_job_title_id: null, ordered_job_title_ids: [] })

export const TEXTS = {
  interest: {
    interested: 'מעוניין/ת',
    not_interested: 'לא מעוניין/ת',
    unknown: 'לא ידוע (לא נשאל/ה)'
  },
  rankCap: 'אפשר לדרג עד חמישה תפקידים',
  saveRanks: 'שמירת סדר',
  primary: 'ראשי',
  ownerPrefix: 'הסדר נקבע ע"י: ',
  owner: { bot: 'הבוט', recruiter: 'מגייסת' },
  ownerNone: 'הסדר טרם נקבע',
  editor: { recruiter: 'נערך ע"י מגייסת', applicant: 'מהמועמד/ת', bot: 'מהבוט' },
  unsaved: 'לא נשמר',
  ranksUnsaved: 'יש שינויים בסדר שלא נשמרו',
  ranksSaved: 'הסדר נשמר',
  ranksNotSupported: 'השרת עדיין לא תומך בשמירת סדר — הסדר לא נשמר',
  ranksRejected: 'הסדר לא נשמר: ',
  ranksFailed: 'שמירת הסדר נכשלה — נסי שוב',
  rowsFailed: 'חלק מהתפקידים לא נשמרו — השורות המסומנות "לא נשמר" עדיין פתוחות, נסי שוב',
  deleteFailed: 'מחיקת התפקיד נכשלה — נסי שוב',
  loadFailed: 'טעינת התפקידים נכשלה — סגרי ופתחי שוב'
}

const isNil = v => v === null || v === undefined
const has = (obj, key) => !!obj && Object.prototype.hasOwnProperty.call(obj, key)

// ---- GET shape --------------------------------------------------------------
// The single place to adapt the list response. Accepts today's bare array or
// an object { job_titles | applicant_job_titles | data: [...], profile }.
export const normalizeJobTitlesResponse = response => {
  if (Array.isArray(response)) return { rows: response, profile: null }
  if (response && typeof response === 'object') {
    const rows = response.job_titles || response.applicant_job_titles || response.data || []
    return { rows: Array.isArray(rows) ? rows : [], profile: response.profile || null }
  }
  return { rows: [], profile: null }
}

export const resolveProfile = profile => {
  const out = Object.assign({}, DEFAULT_PROFILE, profile || {})
  if (out.ranks_version === null || out.ranks_version === undefined) out.ranks_version = 0
  out.ordered_job_title_ids = Array.isArray(out.ordered_job_title_ids) ? out.ordered_job_title_ids : []
  return out
}

// A real profile answer, not e.g. an HTML fallback page served with 200.
export const isProfileResponse = response =>
  !!response && typeof response === 'object' && !Array.isArray(response) &&
  (has(response, 'ranks_version') || has(response, 'ranks_owner') || has(response, 'ordered_job_title_ids'))

// Secondary signal only (the profile endpoint is authoritative): an embedded
// profile or a row carrying the new fields.
export const isNewServer = (rows, profile) =>
  !!profile || (rows || []).some(r => has(r, 'interest_answered_at_ms') || has(r, 'interest_state'))

// The single place that loads the editor: index + profile in parallel.
// Profile 200 -> new back-end, profile adopted. Profile 404/422/network error
// -> today's back-end (no toast) unless the rows themselves carry the new fields.
// An index failure rejects (the caller shows the load error).
export const loadApplicantJobTitles = async (networkUtils, applicantId) => {
  const profileCall = typeof networkUtils.getApplicantJobTitleProfile === 'function'
    ? Promise.resolve()
      .then(() => networkUtils.getApplicantJobTitleProfile({ applicant_id: applicantId }))
      .then(r => (isProfileResponse(r) ? r : null), () => null)
    : Promise.resolve(null)
  const [response, profileResponse] = await Promise.all([
    networkUtils.getApplicantJobTitle({ applicant_id: applicantId }),
    profileCall
  ])
  const { rows, profile: embedded } = normalizeJobTitlesResponse(response)
  const profile = profileResponse || embedded
  return {
    rows,
    profile: resolveProfile(profile),
    newServer: !!profileResponse || isNewServer(rows, embedded)
  }
}

// ---- interest (three-state) ------------------------------------------------
const INTEREST_VALUES = [INTEREST.INTERESTED, INTEREST.NOT_INTERESTED, INTEREST.UNKNOWN]

// The server's own interest_state wins when present; otherwise derive it.
export const readInterestState = (row, newServer) => {
  if (newServer && INTEREST_VALUES.indexOf(row.interest_state) !== -1) return row.interest_state
  if (row.is_interested === true) return INTEREST.INTERESTED
  if (!newServer) return INTEREST.NOT_INTERESTED
  return isNil(row.interest_answered_at_ms) ? INTEREST.UNKNOWN : INTEREST.NOT_INTERESTED
}

// New back-end: interest_state. Older back-end only knows the boolean; it
// cannot store "unknown", so nothing is sent for it.
export const interestPayload = (state, newServer) => {
  if (newServer) return { interest_state: state }
  if (state === INTEREST.UNKNOWN) return {}
  return { is_interested: state === INTEREST.INTERESTED }
}

export const interestLabel = state => TEXTS.interest[state] || TEXTS.interest.unknown

// ---- dates -----------------------------------------------------------------
export const yearsSince = (startedAt, now) => {
  if (!startedAt) return null
  return moment(now).diff(moment(startedAt), 'years', true).toFixed(1)
}

// Computed once, from what the recruiter typed. Fractional years are converted
// to whole months explicitly (moment would silently round fractional years).
export const startedAtFromInput = (selector, value, now) => {
  if (isNil(value) || value === '') return null
  if (selector === 'number') {
    const years = parseFloat(value)
    if (!isFinite(years) || years < 0 || years > MAX_YEARS) return null
    return moment(now).subtract(Math.round(years * 12), 'months').format()
  }
  if (selector === 'date') {
    const date = moment(value, 'YYYY-MM-DD', true)
    if (!date.isValid() || date.isAfter(moment(now))) return null
    return date.format()
  }
  return null
}

// ---- editable rows -----------------------------------------------------------
const snapshot = row => ({
  interest_state: row.interest_state,
  has_experience: row.has_experience,
  still_active: row.still_active
})

export const toEditableRow = (serverRow, newServer, now) => {
  const jobTitle = serverRow.job_title || {}
  const row = Object.assign({}, jobTitle, {
    id: !isNil(jobTitle.id) ? jobTitle.id : serverRow.job_title_id,
    started_at: yearsSince(serverRow.started_at, now),
    still_active: !serverRow.ended_at,
    has_experience: serverRow.has_experience,
    interest_state: readInterestState(serverRow, newServer),
    is_approved: serverRow.is_approved,
    approval_date: serverRow.approval_date,
    applicant_comment: serverRow.applicant_comment,
    title_rank: isNil(serverRow.title_rank) ? null : serverRow.title_rank,
    last_editor: serverRow.last_editor || null,
    edit_version: serverRow.edit_version,
    server: true,
    experienceSelector: 'number',
    started_edited: false
  })
  row.is_interested = row.interest_state === INTEREST.INTERESTED
  row.original = snapshot(row)
  return row
}

// New back-end: a new title starts UNKNOWN. Today's back-end cannot store
// "unknown" (its column default is false = "not interested"), so there it keeps
// the previous default, interested, and the screen shows what will be stored.
export const newRowInterest = newServer => (newServer ? INTEREST.UNKNOWN : INTEREST.INTERESTED)

export const newEditableRow = (jobTitle, newServer) => Object.assign({}, jobTitle, {
  started_at: null,
  still_active: true,
  has_experience: true,
  interest_state: newRowInterest(newServer),
  is_interested: newRowInterest(newServer) === INTEREST.INTERESTED,
  title_rank: null,
  last_editor: null,
  server: false,
  experienceSelector: 'number',
  started_edited: true,
  original: null
})

// Typing years/dates means the title has experience: a loaded row with
// has_experience false/null becomes true, and both fields are sent.
export const setRowStarted = (row, value) => {
  row.started_at = value
  row.started_edited = true
  row.has_experience = true
}

export const setRowInterest = (row, state) => {
  row.interest_state = state
  row.is_interested = state === INTEREST.INTERESTED
}

// Only the fields the recruiter changed. {} = nothing to send.
export const rowChanges = (row, newServer, now) => {
  const orig = row.original || {}
  const isNew = !row.server
  const changes = {}
  if (isNew || row.interest_state !== orig.interest_state) {
    Object.assign(changes, interestPayload(row.interest_state, newServer))
  }
  if (isNew || row.has_experience !== orig.has_experience) changes.has_experience = !!row.has_experience
  if (row.has_experience && row.started_edited) {
    const startedAt = startedAtFromInput(row.experienceSelector, row.started_at, now)
    if (startedAt !== null) changes.started_at = startedAt
  }
  if (isNew ? !row.still_active : row.still_active !== orig.still_active) {
    changes.ended_at = row.still_active ? null : moment(now).format()
  }
  return changes
}

export const isRowDirty = (row, newServer, now) => !row.server || Object.keys(rowChanges(row, newServer, now)).length > 0

export const isRowValid = (row, now) => {
  if (!row.has_experience || !row.started_edited) return true
  return startedAtFromInput(row.experienceSelector, row.started_at, now) !== null
}

// [{ method: 'post' | 'put', row, params }] — unchanged rows produce nothing.
export const buildRowRequests = (rows, applicantId, newServer, now) => rows.reduce((acc, row) => {
  const changes = rowChanges(row, newServer, now)
  if (row.server && Object.keys(changes).length === 0) return acc
  acc.push({
    method: row.server ? 'put' : 'post',
    row,
    params: Object.assign({ applicant_id: applicantId, job_title_id: row.id }, changes)
  })
  return acc
}, [])

// After a successful save the row's current values become its baseline.
export const markRowSaved = (row, newServer) => {
  row.server = true
  row.started_edited = false
  row.original = snapshot(row)
  if (newServer) row.last_editor = 'recruiter'
}

// ---- ranks ---------------------------------------------------------------
export const initialRanks = (rows, profile) => {
  const ids = rows.map(r => r.id)
  const fromProfile = profile && Array.isArray(profile.ordered_job_title_ids)
    ? profile.ordered_job_title_ids.filter((id, i, all) => ids.indexOf(id) !== -1 && all.indexOf(id) === i).slice(0, MAX_RANKED)
    : []
  const ranked = fromProfile.length ? fromProfile : rows
    .filter(r => !isNil(r.title_rank))
    .slice()
    .sort((a, b) => a.title_rank - b.title_rank)
    .slice(0, MAX_RANKED)
    .map(r => r.id)
  const primary = profile && ranked.indexOf(profile.primary_job_title_id) !== -1 ? profile.primary_job_title_id : null
  return { ranked, primary }
}

// Ranked first (in rank order), then the rest in their existing order.
export const orderRows = (rows, ranked) => {
  const byId = {}
  rows.forEach(r => { byId[r.id] = r })
  const head = ranked.map(id => byId[id]).filter(Boolean)
  return head.concat(rows.filter(r => ranked.indexOf(r.id) === -1))
}

// Up: swap with the previous ranked title; an unranked title joins the end of
// the ranked list unless it is already full.
export const moveUp = (ranked, id) => {
  const i = ranked.indexOf(id)
  if (i === 0) return { ranked: ranked.slice(), error: null }
  if (i > 0) {
    const next = ranked.slice()
    next[i - 1] = ranked[i]
    next[i] = ranked[i - 1]
    return { ranked: next, error: null }
  }
  if (ranked.length >= MAX_RANKED) return { ranked: ranked.slice(), error: 'cap' }
  return { ranked: ranked.concat([id]), error: null }
}

// Down: swap with the next ranked title; the last ranked title leaves the ranking.
export const moveDown = (ranked, id) => {
  const i = ranked.indexOf(id)
  if (i === -1) return { ranked: ranked.slice(), error: null }
  if (i === ranked.length - 1) return { ranked: ranked.slice(0, i), error: null }
  const next = ranked.slice()
  next[i + 1] = ranked[i]
  next[i] = ranked[i + 1]
  return { ranked: next, error: null }
}

export const keepPrimary = (ranked, primary) => (ranked.indexOf(primary) === -1 ? null : primary)

export const ranksChanged = (ranked, primary, serverRanked, serverPrimary) =>
  primary !== serverPrimary || ranked.length !== serverRanked.length || ranked.some((id, i) => id !== serverRanked[i])

export const buildRanksRequest = (applicantId, ranked, primary) => {
  if (ranked.length > MAX_RANKED) throw new Error('more than ' + MAX_RANKED + ' ranked job titles')
  const body = { applicant_id: applicantId, ordered_job_title_ids: ranked.slice() }
  if (!isNil(primary)) body.primary_job_title_id = primary
  return body
}

export const interpretRanksResponse = (response, sent) => {
  if (!response || response.status !== 'applied') return null
  return {
    ranked: Array.isArray(response.ordered_job_title_ids) ? response.ordered_job_title_ids.slice(0, MAX_RANKED) : sent.ordered_job_title_ids,
    primary: has(response, 'primary_job_title_id') ? response.primary_job_title_id : (isNil(sent.primary_job_title_id) ? null : sent.primary_job_title_id),
    ranks_version: has(response, 'ranks_version') ? response.ranks_version : null
  }
}

export const ranksErrorMessage = error => {
  const res = error && error.response
  if (res && res.status === 404) return TEXTS.ranksNotSupported
  if (res && res.status >= 400 && res.status < 500 && res.data && res.data.message) return TEXTS.ranksRejected + res.data.message
  return TEXTS.ranksFailed
}

export const ownerText = owner => (TEXTS.owner[owner] ? TEXTS.ownerPrefix + TEXTS.owner[owner] : TEXTS.ownerNone)
export const editorTag = editor => TEXTS.editor[editor] || ''
